import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import EmptyState from '../components/ui/EmptyState';
import LoadingScreen from '../components/ui/LoadingScreen';
import { IconPlus } from '../components/ui/Icons';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

const getProductId = (product) => product?.productId || product?._id || '';

function Products() {
  const toast = useToast();
  const confirm = useConfirm();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    defaultMongoUri: '',
    databaseName: '',
    status: 'active',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.post('/product/list', {});
      setProducts(response.data.data?.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData({ name: '', defaultMongoUri: '', databaseName: '', status: 'active' });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      defaultMongoUri: product.defaultMongoUri || '',
      databaseName: product.databaseName || '',
      status: product.status || 'active',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.post('/product/update', {
          productId: getProductId(editingProduct),
          name: formData.name,
          defaultMongoUri: formData.defaultMongoUri || null,
          databaseName: formData.databaseName || null,
          status: formData.status,
        });
        toast.success('Product updated successfully');
      } else {
        await api.post('/product/create', {
          name: formData.name,
          defaultMongoUri: formData.defaultMongoUri || null,
          databaseName: formData.databaseName || null,
          status: formData.status,
        });
        toast.success('Product created successfully');
      }
      setShowModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    }
  };

  const handleDelete = async (product) => {
    const productId = getProductId(product);
    const confirmed = await confirm({
      title: 'Delete product',
      message: `Are you sure you want to delete "${product.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.post('/product/delete', { productId });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.message || 'Failed to delete product');
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading products..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Products"
        description="Create and manage products in your multi-tenant platform."
        action={
          <button type="button" onClick={handleCreate} className="btn btn--primary">
            <IconPlus width={18} height={18} />
            Create Product
          </button>
        }
      />

      <div className="card">
        {products.length === 0 ? (
          <EmptyState
            title="No products yet"
            description="Create your first product to start onboarding tenants."
            action={
              <button type="button" onClick={handleCreate} className="btn btn--primary">
                <IconPlus width={18} height={18} />
                Create Product
              </button>
            }
          />
        ) : (
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Database</th>
                  <th>Default URI</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const productId = getProductId(product);
                  return (
                  <tr key={productId}>
                    <td data-label="Name">{product.name}</td>
                    <td data-label="Database">{product.databaseName || '—'}</td>
                    <td data-label="Default URI" className="cell-mono" title={product.defaultMongoUri || 'Not set'}>
                      {product.defaultMongoUri || <span className="cell-muted">Not set</span>}
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={product.status} />
                    </td>
                    <td data-label="Created">{new Date(product.createdAt).toLocaleDateString()}</td>
                    <td data-label="Actions" className="cell-actions">
                      <div className="btn-group">
                        <Link
                          to={`/email-config?productId=${encodeURIComponent(productId)}`}
                          className="btn btn--secondary btn--sm"
                        >
                          Email
                        </Link>
                        <Link
                          to={`/whatsapp-config?productId=${encodeURIComponent(productId)}`}
                          className="btn btn--secondary btn--sm"
                        >
                          WhatsApp
                        </Link>
                        <button type="button" onClick={() => handleEdit(product)} className="btn btn--secondary btn--sm">
                          Edit
                        </button>
                        <button type="button" onClick={() => handleDelete(product)} className="btn btn--danger btn--sm">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <Modal
          title={editingProduct ? 'Edit Product' : 'Create Product'}
          subtitle={editingProduct ? 'Update product configuration' : 'Add a new product to the platform'}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Enter product name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Database Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.databaseName}
                onChange={(e) => setFormData({ ...formData, databaseName: e.target.value })}
                placeholder="Enter database name"
                disabled={!!editingProduct}
              />
              <span className="form-hint">Optional on create. Auto-generated from name if empty.</span>
            </div>
            <div className="form-group">
              <label className="form-label">Default MongoDB URI</label>
              <input
                type="text"
                className="form-input"
                value={formData.defaultMongoUri}
                onChange={(e) => setFormData({ ...formData, defaultMongoUri: e.target.value })}
                placeholder="Enter MongoDB connection URI"
              />
              <span className="form-hint">Connection string without database name.</span>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
            <div className="modal__footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn--secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn--primary">
                {editingProduct ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default Products;
